/**
 * Validated API Client
 *
 * Wrapper around the base API client that validates responses against Zod schemas.
 * - Dev/staging: Full validation with toast (local only) + console errors
 * - Production: No validation overhead (pass-through)
 *
 * @module lib/api/validated-client
 */

import { z, ZodSchema } from 'zod';
import { AxiosRequestConfig } from 'axios';
import { apiClient } from './client';
import { findEndpoint, HttpMethod } from '@lov/shared';
import toast from 'react-hot-toast';

// ============================================
// ENVIRONMENT DETECTION
// ============================================

const ENV = import.meta.env.VITE_ENV || 'development';
const IS_LOCAL =
  ENV === 'development' &&
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const IS_DEV_OR_STAGING = ENV === 'development' || ENV === 'staging';
const IS_PRODUCTION = ENV === 'production';

// ============================================
// VALIDATION ERROR TRACKING
// ============================================

interface ValidationError {
  endpoint: string;
  method: HttpMethod;
  errors: z.ZodIssue[];
  response: unknown;
  timestamp: string;
}

// Track validation errors for debugging (last 50)
const validationErrors: ValidationError[] = [];

// ============================================
// ERROR LOGGING & NOTIFICATION
// ============================================

/**
 * Log validation error to console (dev/staging only)
 */
function logValidationError(error: ValidationError): void {
  if (IS_PRODUCTION) return;

  console.group(
    `%c[API Contract Violation] ${error.method} ${error.endpoint}`,
    'color: #EF4444; font-weight: bold; font-size: 14px'
  );
  console.error('Validation errors:', JSON.stringify(error.errors, null, 2));
  console.log('Actual response:', error.response);
  console.log('Timestamp:', error.timestamp);
  console.groupEnd();
}

/**
 * Show toast notification (local development only)
 */
function showValidationToast(error: ValidationError): void {
  if (!IS_LOCAL) return;

  // Show first 2 field errors
  const fieldErrors = error.errors
    .slice(0, 2)
    .map((e) => {
      const field = e.path.join('.');
      return field ? `${field}: ${e.message}` : e.message;
    })
    .join('\n');

  const moreErrors = error.errors.length > 2 ? `\n+${error.errors.length - 2} more errors` : '';
  const message = `API Contract Mismatch\n${error.method} ${error.endpoint}\n\n${fieldErrors}${moreErrors}`;

  toast.error(message, {
    duration: 8000,
    position: 'bottom-right',
    style: {
      background: '#FEF2F2',
      color: '#991B1B',
      border: '2px solid #F87171',
      maxWidth: '500px',
      fontSize: '13px',
      whiteSpace: 'pre-line',
      fontFamily: 'monospace',
    },
    icon: '⚠️',
  });
}

// ============================================
// RESPONSE VALIDATION
// ============================================

/**
 * Validate response against schema
 * Returns the original data regardless of validation result (non-blocking)
 */
function validateResponse<T>(
  method: HttpMethod,
  path: string,
  data: unknown,
  explicitSchema?: ZodSchema<T>
): T {
  // Skip validation in production
  if (IS_PRODUCTION) return data as T;

  // Remove /api prefix if present for matching
  const normalizedPath = path.replace(/^\/api/, '');

  // Find schema from registry if not provided
  const endpoint = findEndpoint(method, normalizedPath);
  const schemaToUse = explicitSchema || endpoint?.responseSchema;

  if (!schemaToUse) {
    // No schema defined — silently pass through. Schemas are added incrementally
    // as endpoints are stabilised. Suppressing the warning to avoid console noise.
    return data as T;
  }

  const result = schemaToUse.safeParse(data);

  if (!result.success) {
    const error: ValidationError = {
      endpoint: normalizedPath,
      method,
      errors: result.error.errors,
      response: data,
      timestamp: new Date().toISOString(),
    };

    // Keep last 50 errors
    if (validationErrors.length >= 50) {
      validationErrors.shift();
    }
    validationErrors.push(error);

    logValidationError(error);
    showValidationToast(error);
  }

  // Always return original data - validation is non-blocking
  return data as T;
}

// ============================================
// VALIDATED API CLIENT
// ============================================

/**
 * Validated API client wrapper
 * Drop-in replacement for apiClient with automatic response validation
 */
export const validatedApi = {
  /**
   * GET request with response validation
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.get<T>(url, config);
    return validateResponse<T>('GET', url, response.data);
  },

  /**
   * POST request with response validation
   */
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.post<T>(url, data, config);
    return validateResponse<T>('POST', url, response.data);
  },

  /**
   * PUT request with response validation
   */
  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.put<T>(url, data, config);
    return validateResponse<T>('PUT', url, response.data);
  },

  /**
   * PATCH request with response validation
   */
  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.patch<T>(url, data, config);
    return validateResponse<T>('PATCH', url, response.data);
  },

  /**
   * DELETE request with response validation
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.delete<T>(url, config);
    return validateResponse<T>('DELETE', url, response.data);
  },
};

// ============================================
// DEBUG UTILITIES
// ============================================

/**
 * Get all validation errors (for debugging)
 */
export function getValidationErrors(): ValidationError[] {
  return [...validationErrors];
}

/**
 * Clear validation error history
 */
export function clearValidationErrors(): void {
  validationErrors.length = 0;
}

// Make available in dev console (dev/staging only)
if (IS_DEV_OR_STAGING && typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__apiValidationErrors = {
    get: getValidationErrors,
    clear: clearValidationErrors,
    errors: validationErrors,
  };

  if (IS_LOCAL) {
    console.log(
      '%c[API Validation] Debug utilities available:',
      'color: #3B82F6; font-weight: bold',
      '\n  window.__apiValidationErrors.get()',
      '\n  window.__apiValidationErrors.clear()'
    );
  }
}
